// Design System - Navigation Principale Premium
// Stratégie couleur: BLEU = structure / ORANGE = action
// Mobile & Desktop: Navigation élégante avec underline orange sur l'onglet actif
// IMMERSIVE SCROLL: Navigation disparaît au scroll down, réapparaît au scroll up

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Users, Store, Megaphone, Network, Briefcase, LayoutDashboard, Building2,
  Zap, TrendingUp, Sparkles, ShoppingBag, Menu, SlidersHorizontal, Clock, AlertTriangle, PlusCircle,
  Globe, ChevronDown, X, Search, Star, Award, ClipboardList, Video, Image as ImageIcon, MessageCircle, Calendar,
  MessageSquare, Wrench, MapPin
} from 'lucide-react';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';

// Configuration des 20 univers (même que mobile)
const UNIVERS_DATA = [
  {
    group: 'OUVERT MAINTENANT',
    items: [
      { id: 'abracadabati', name: 'AbracadaBati', desc: 'Bâtiment & BTP', icon: '🔨', iconBg: 'linear-gradient(135deg, #E8650A, #f07a2a)', badge: 'Bêta Ouverte', badgeColor: '#22c55e', active: true, path: '/univers' },
    ]
  },
  {
    group: 'BÊTA JUILLET 2026',
    items: [
      { id: 'abracadahome', name: 'AbracadaHome', desc: 'Décoration & Maison', icon: '🏠', iconBg: '#E6F1FB', badge: 'Juillet 2026' },
      { id: 'abracadamatch', name: 'AbracadaMatch', desc: 'Rencontres certifiées', icon: '❤️', iconBg: '#FCEBEB', badge: 'Juillet 2026' },
      { id: 'abracadaevent', name: 'AbracadaEvent', desc: 'Événementiel & Fêtes', icon: '🎉', iconBg: '#FAECE7', badge: 'Juillet 2026' },
      { id: 'abracadazoo', name: 'AbracadaZoo', desc: 'Animaux & Nature', icon: '🐾', iconBg: '#EAF3DE', badge: 'Juillet 2026' },
    ]
  },
  {
    group: 'BÊTA SEPTEMBRE 2026',
    items: [
      { id: 'abracadatravel', name: 'AbracadaTravel', desc: 'Voyage & Expériences', icon: '✈️', iconBg: '#E6F1FB', badge: 'Sept. 2026' },
      { id: 'abracadafashion', name: 'AbracadaFashion', desc: 'Mode & Luxe', icon: '👗', iconBg: '#F1EFE8', badge: 'Sept. 2026' },
      { id: 'abracadatech', name: 'AbracadaTech', desc: 'High-Tech & Électronique', icon: '💻', iconBg: '#E6F1FB', badge: 'Sept. 2026' },
      { id: 'abracadarealty', name: 'AbracadaRealty', desc: 'Immobilier Mondial', icon: '🏡', iconBg: '#EEEDFE', badge: 'Sept. 2026' },
      { id: 'abracadabeauty', name: 'AbracadaBeauty', desc: 'Beauté & Cosmétiques', icon: '✨', iconBg: '#FBEAF0', badge: 'Sept. 2026' },
      { id: 'abracadasecret', name: 'AbracadaSecret', desc: 'Adultes 18+', icon: '🔐', iconBg: '#FBEAF0', badge: 'Sept. 2026' },
      { id: 'abracadasport', name: 'AbracadaSport', desc: 'Sport & Fitness', icon: '⚽', iconBg: '#FAECE7', badge: 'Sept. 2026' },
    ]
  },
  {
    group: 'BÊTA JANVIER 2027',
    items: [
      { id: 'abracadagreen', name: 'AbracadaGreen', desc: 'Écologie & Développement durable', icon: '🌿', iconBg: '#EAF3DE', badge: 'Jan. 2027' },
      { id: 'abracadafood', name: 'AbracadaFood', desc: 'Alimentation & Restauration', icon: '🍽️', iconBg: '#FFF3EC', badge: 'Jan. 2027' },
      { id: 'abracadacar', name: 'AbracadaCar', desc: 'Automobile & Moto', icon: '🚗', iconBg: '#EEEDFE', badge: 'Jan. 2027' },
      { id: 'abracadaboat', name: 'AbracadaBoat', desc: 'Nautisme & Mer', icon: '⛵', iconBg: '#E1F5EE', badge: 'Jan. 2027' },
      { id: 'abracadahealth', name: 'AbracadaHealth', desc: 'Santé & Bien-être', icon: '❤️‍🩹', iconBg: '#FBEAF0', badge: 'Jan. 2027' },
      { id: 'abracadatoys', name: 'AbracadaToys', desc: 'Jouets & Enfants', icon: '🧸', iconBg: '#FCEBEB', badge: 'Jan. 2027' },
      { id: 'abracadalearn', name: 'AbracadaLearn', desc: 'Formation & Coaching', icon: '📚', iconBg: '#FAEEDA', badge: 'Jan. 2027' },
      { id: 'abracadaart', name: 'AbracadaArt', desc: 'Art & Culture', icon: '🎨', iconBg: '#EEEDFE', badge: 'Jan. 2027' },
    ]
  },
];

// Navigation principale - Univers en premier
// Note: "Réseau Pro" a été déplacé dans le sous-menu Espace Pro
// Séparation: "Acheter à un pro" (boutiques) vs "Entre particuliers" (C2C)
const NAV_ITEMS = [
  { path: '/univers', label: 'Univers', icon: Globe, isUniversDropdown: true },
  { path: '/artisans', label: 'Artisans', icon: Users },
  { path: '/produits', label: 'Acheter à un pro', icon: Store },
  { path: '/entre-particuliers', label: 'Entre particuliers', icon: Users, isParticuliers: true },
  { path: '/reseau', label: 'Réseau', icon: Network },
  { path: '/emploi', label: 'Emploi', icon: Briefcase },
  { path: '/pro', label: 'Espace Pro', icon: LayoutDashboard },
];

// Sous-menu Marketplace (barre horizontale) - "Acheter à un pro"
const MARKETPLACE_SUBMENU = [
  { path: '/produits', label: 'Produits', icon: ShoppingBag },
  { path: '/prestations', label: 'Prestations', icon: Wrench },
  { path: null, label: 'Rayons', icon: Menu, isRayonsButton: true },
  { path: null, label: 'Trouve en 3 clics', icon: null, isFilterButton: true },
  { path: '/ventes-flash', label: 'Ventes Flash', icon: Zap },
  { path: '/meilleures-ventes', label: 'Meilleures ventes', icon: TrendingUp },
  { path: '/nouveautes', label: 'Nouveautés', icon: Sparkles },
];

// Sous-menu Artisans
const ARTISANS_SUBMENU = [
  { path: '/artisans', label: 'Trouver', icon: Search, isFilterButton: true },
  { path: '/decrire-projet', label: 'Devis', icon: ClipboardList, isDevisButton: true },
  { path: '/artisans/carte', label: 'Carte', icon: null, isCarteButton: true },
];

// Sous-menu Entre Particuliers (petites annonces C2C)
const PARTICULIERS_SUBMENU = [
  { path: '/entre-particuliers', label: 'Accueil', icon: Home },
  { path: null, label: 'Catégories', icon: Menu, isCategoriesButton: true },
  { path: null, label: 'Trouve ton annonce en 3 clics', icon: null, isParticuliersFilterButton: true },
  { path: '/entre-particuliers/recentes', label: 'Annonces récentes', icon: Clock },
  { path: '/entre-particuliers/urgentes', label: 'Urgent', icon: AlertTriangle },
  { path: '/entre-particuliers/deposer', label: 'Déposer une annonce', icon: PlusCircle },
];

// Sous-menu Réseau Social
const RESEAU_SUBMENU = [
  { path: '/reseau', label: "Fil d'actualité", icon: Home, isAccueilReseau: true },
  { path: '/reseau/cinema', label: 'Cinéma', icon: Video, isCinemaReseau: true },
  { path: '/reseau/galerie', label: 'Galerie', icon: ImageIcon },
  { path: '/reseau/salon', label: 'Salon', icon: MessageCircle },
  { path: '/reseau/evenements', label: 'Événement', icon: Calendar },
];

// Sous-menu Emploi
const EMPLOI_SUBMENU = [
  { path: '/emploi', label: 'Toutes les offres BTP', icon: Home, isAccueilEmploi: true },
  { path: null, label: 'Trouve ton emploi en 3 clics', icon: null, isEmploiFilterButton: true },
  { path: '/emploi/entreprises', label: 'Entreprises qui recrutent', icon: Building2 },
  { path: '/emploi/deposer-cv', label: 'Déposer mon CV', icon: ClipboardList },
];

export default function MainNavigation({ onOpenRayonsMenu, onOpenFilters, onOpenParticuliersCategories, onOpenParticuliersFilters }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isHidden, shouldShowBanner } = useImmersiveScrollContext();
  const currentPath = '/' + (location.pathname.split('/')[1] || '');
  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const [showUniversDropdown, setShowUniversDropdown] = useState(false);
  const universDropdownRef = useRef(null);
  
  // Détecte si on est dans la section Marketplace (inclut Prestations)
  const isMarketplaceSection = location.pathname.startsWith('/produits') || 
    location.pathname.startsWith('/prestations') ||
    location.pathname.startsWith('/ventes-flash') ||
    location.pathname.startsWith('/meilleures-ventes') ||
    location.pathname.startsWith('/nouveautes') ||
    location.pathname.includes('marketplace');

  // Détecte si on est dans la section Artisans
  const isArtisansSection = location.pathname.startsWith('/artisans') || 
    location.pathname === '/decrire-projet';

  // Détecte si on est dans la section Entre Particuliers (C2C)
  const isParticuliersSection = location.pathname.startsWith('/particuliers') ||
    location.pathname.startsWith('/entre-particuliers') ||
    location.pathname.startsWith('/annonces');

  // Détecte si on est dans la section Réseau
  const isReseauSection = location.pathname === '/reseau' || 
    location.pathname.startsWith('/reseau/');

  // Détecte si on est dans la section Emploi
  const isEmploiSection = location.pathname === '/emploi' || 
    location.pathname.startsWith('/emploi/');

  // Vérifier la position du scroll pour afficher les indicateurs
  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftFade(scrollLeft > 10);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
      setTimeout(checkScrollPosition, 100);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, []);

  // Scroll vers l'élément actif au chargement
  useEffect(() => {
    if (scrollRef.current) {
      const activeItem = scrollRef.current.querySelector('[data-active="true"]');
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentPath]);

  // Fermer le dropdown Univers quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (universDropdownRef.current && !universDropdownRef.current.contains(e.target)) {
        setShowUniversDropdown(false);
      }
    };
    if (showUniversDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUniversDropdown]);

  // Handler pour clic sur un univers
  const handleUniversClick = (univers) => {
    if (univers.active) {
      setShowUniversDropdown(false);
      navigate(univers.path);
    }
  };

  // Vérifier si on est sur mobile ou tablette
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Calcul du top selon le device
  // Mobile: header 52px + banner 36px = 88px (immersif)
  // Tablette: header 64px + banner 36px = 100px (banner toujours visible)
  const getTopPosition = () => {
    if (isMobile) {
      return shouldShowBanner ? '88px' : '52px';
    }
    if (isTablet) {
      return '100px'; // Banner toujours visible sur tablette
    }
    return '0px';
  };

  // Position du top pour desktop (bandeau 40px + header 64px = 104px)
  const getDesktopTop = () => '104px';

  return (
    <nav 
      className="navbar z-40 main-navigation"
      style={{
        // Toutes résolutions: position fixed
        position: 'fixed',
        top: (isMobile || isTablet) ? getTopPosition() : getDesktopTop(),
        left: 0,
        right: 0,
        backgroundColor: '#FFF8F4',
        borderBottom: '1px solid #FCE4D4',
        transition: 'transform 0.22s ease, top 0.22s ease, opacity 0.18s ease',
        // Animation immersive uniquement sur mobile
        transform: (isMobile && isHidden) ? 'translateY(-200px)' : 'translateY(0)',
        opacity: (isMobile && isHidden) ? 0 : 1,
        pointerEvents: (isMobile && isHidden) ? 'none' : 'auto',
        willChange: 'transform, opacity',
        zIndex: 90,
      }}
    >
      {/* Mobile + Tablet navigation */}
      <div className="lg:hidden">
        <div className={(isArtisansSection || isReseauSection) ? "relative" : "max-w-7xl mx-auto relative"}>
          {/* Indicateur scroll droite */}
          <div 
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: 'linear-gradient(to left, #FFF8F4, transparent)',
              pointerEvents: 'none',
              zIndex: 10,
              opacity: showRightFade ? 1 : 0,
            }}
          />
          
          {/* Navigation scrollable - SOUS-MENU selon la section */}
          <div 
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              marginLeft: (isArtisansSection || isMarketplaceSection || isReseauSection) ? '0px' : (isTablet ? '16px' : '8px'),
              paddingLeft: (isArtisansSection || isMarketplaceSection || isReseauSection) ? '0px' : '0px',
              paddingRight: '16px',
              height: isTablet ? '44px' : '44px',
              display: 'flex',
              alignItems: 'center',
              gap: '0px',
            }}
          >
            {/* Si section Marketplace -> afficher sous-menu Marketplace PREMIUM */}
            {isMarketplaceSection ? (
              <>
                {MARKETPLACE_SUBMENU.map((subItem, index) => {
                  const isSubActive = subItem.path && location.pathname === subItem.path;
                  const isFirstItem = index === 0;
                  const isLastItem = index === MARKETPLACE_SUBMENU.length - 1;
                  
                  // Bouton Rayons
                  if (subItem.isRayonsButton) {
                    return (
                      <React.Fragment key="rayons-btn-mobile">
                        <button
                          onClick={() => onOpenRayonsMenu?.()}
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                          data-testid="nav-mobile-marketplace-rayons"
                        >
                          {subItem.label}
                        </button>
                      </React.Fragment>
                    );
                  }
                  
                  // Bouton Filtres
                  if (subItem.isFilterButton) {
                    return (
                      <React.Fragment key="filter-btn-mobile">
                        <button
                          onClick={() => onOpenFilters?.()}
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}
                          data-testid={`nav-mobile-marketplace-${index}`}
                        >
                          {subItem.label}
                        </button>
                      </React.Fragment>
                    );
                  }
                  
                  // Items normaux
                  return (
                    <React.Fragment key={subItem.path || `marketplace-${index}`}>
                      <Link
                        to={subItem.path}
                        style={{
                          fontSize: '13px',
                          fontWeight: isSubActive ? 600 : 500,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease',
                        }}
                        data-testid={`nav-mobile-marketplace-${index}`}
                      >
                        {subItem.label}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </>
            ) : isArtisansSection ? (
              /* Si section Artisans -> afficher sous-menu Artisans PREMIUM */
              <>
                {ARTISANS_SUBMENU.map((subItem, index) => {
                  const SubIcon = subItem.icon;
                  const isSubActive = subItem.path && location.pathname === subItem.path;
                  
                  // Bouton "Artisans du mois" grisé (en cours de développement)
                  if (subItem.isComingSoon) {
                    return (
                      <span
                        key="coming-soon-mobile"
                        style={{
                          padding: '0 4px',
                          height: '44px',
                          fontSize: '13px',
                          fontWeight: 400,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                          color: '#C0C0C0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          cursor: 'not-allowed',
                        }}
                        data-testid="nav-mobile-artisans-du-mois"
                      >
                        {subItem.label}
                      </span>
                    );
                  }
                  
                  // Tous les autres items - Style PREMIUM Apple/Stripe
                  // Premier item (Accueil) sans padding gauche pour alignement hamburger
                  const isFirstItem = index === 0;
                  const isLastItem = index === ARTISANS_SUBMENU.length - 1;
                  return (
                    <React.Fragment key={subItem.path || `artisan-${index}`}>
                      <Link
                        to={subItem.isFilterButton ? '#' : subItem.path}
                        onClick={subItem.isFilterButton ? (e) => { e.preventDefault(); onOpenFilters?.(); } : undefined}
                        style={{
                          fontSize: '13px',
                          fontWeight: isSubActive ? 600 : 500,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease',
                        }}
                        data-testid={`nav-mobile-artisans-${index}`}
                      >
                        {subItem.label}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </>
            ) : isReseauSection ? (
              /* Si section Réseau -> afficher sous-menu Réseau PREMIUM */
              <>
                {RESEAU_SUBMENU.map((subItem, index) => {
                  const SubIcon = subItem.icon;
                  const isSubActive = subItem.path && (
                    location.pathname === subItem.path || 
                    (subItem.path === '/reseau' && location.pathname === '/reseau')
                  );
                  const isFirstItem = index === 0;
                  const isLastItem = index === RESEAU_SUBMENU.length - 1;
                  
                  return (
                    <React.Fragment key={subItem.path || `reseau-${index}`}>
                      <Link
                        to={subItem.path}
                        style={{
                          fontSize: '13px',
                          fontWeight: isSubActive ? 600 : 500,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease',
                        }}
                        data-testid={`nav-mobile-reseau-${index}`}
                      >
                        {subItem.label}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </>
            ) : isEmploiSection ? (
              /* Si section Emploi -> afficher sous-menu Emploi PREMIUM */
              <>
                {EMPLOI_SUBMENU.map((subItem, index) => {
                  const isSubActive = subItem.path && location.pathname === subItem.path;
                  
                  return (
                    <React.Fragment key={subItem.path || `emploi-${index}`}>
                      <Link
                        to={subItem.isEmploiFilterButton ? '#' : subItem.path}
                        onClick={subItem.isEmploiFilterButton ? (e) => { e.preventDefault(); onOpenFilters?.(); } : undefined}
                        style={{
                          fontSize: '13px',
                          fontWeight: isSubActive ? 600 : 500,
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease',
                        }}
                        data-testid={`nav-mobile-emploi-${index}`}
                      >
                        {subItem.label}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              /* Sinon afficher le menu standard */
              NAV_ITEMS.map((item, index) => {
              const isActive = currentPath === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              const Icon = item.icon;
              
              // Univers avec dropdown pour tablette
              if (item.isUniversDropdown && isTablet) {
                return (
                  <div key={item.path} className="relative flex-shrink-0" ref={universDropdownRef}>
                    <button
                      onClick={() => setShowUniversDropdown(!showUniversDropdown)}
                      data-active={isActive}
                      className={`navbar-item navbar-item-mobile whitespace-nowrap ${isActive ? 'active' : ''}`}
                      style={{
                        paddingLeft: '0px',
                        paddingRight: '12px',
                        transform: 'translateY(3px)',
                        height: '52px',
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 500,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        color: isActive ? '#E8650A' : '#555555',
                        borderBottom: isActive ? '2px solid #E8650A' : '2px solid transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      data-testid="nav-univers-dropdown-tablet"
                    >
                      <Icon size={16} style={{ color: isActive ? '#E8650A' : '#888888' }} />
                      <span>{item.label}</span>
                      <ChevronDown size={12} className={`transition-transform ${showUniversDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Univers Tablette */}
                    {showUniversDropdown && (
                      <div 
                        className="fixed left-4 right-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[9999]"
                        style={{ top: '130px', maxHeight: '70vh' }}
                      >
                        <div className="p-3 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #e8f4fc 0%, #f0f7ff 100%)' }}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-bold text-sm text-[#1a2a4a]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                1 compte certifié = 20 univers
                              </h3>
                              <p className="text-xs font-semibold text-[#E8650A]">Bienvenue chez toi.</p>
                            </div>
                            <button onClick={() => setShowUniversDropdown(false)} className="p-1.5 rounded-full hover:bg-gray-100">
                              <X size={16} color="#666" />
                            </button>
                          </div>
                        </div>
                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 120px)' }}>
                          {UNIVERS_DATA.map((group) => (
                            <div key={group.group}>
                              <div className="px-3 py-1.5 text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                                {group.group}
                              </div>
                              {group.items.map((univers) => (
                                <button
                                  key={univers.id}
                                  onClick={() => handleUniversClick(univers)}
                                  disabled={!univers.active}
                                  className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left ${
                                    univers.active ? 'bg-gradient-to-r from-orange-50 to-transparent border-l-[3px] border-[#E8650A]' : ''
                                  }`}
                                >
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: univers.iconBg }}>
                                    {univers.icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-xs text-[#1a2a4a] truncate">{univers.name}</div>
                                    <div className={`text-[10px] truncate ${univers.active ? 'text-[#E8650A]' : 'text-[#1a2a4a]'}`}>{univers.desc}</div>
                                  </div>
                                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: univers.badgeColor || '#f0f0f0', color: univers.badgeColor ? '#fff' : '#999' }}>
                                    {univers.badge}
                                  </span>
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-active={isActive}
                  className={`navbar-item navbar-item-mobile whitespace-nowrap flex-shrink-0 ${isActive ? 'active' : ''}`}
                  style={{
                    paddingLeft: index === 0 ? '0px' : (isTablet ? '12px' : '6px'),
                    paddingRight: index === 0 ? '0px' : (isTablet ? '12px' : '6px'),
                    marginLeft: index === 1 ? (isTablet ? '-10px' : '-20px') : '0px',
                    transform: 'translateY(3px)',
                    height: isTablet ? '52px' : '44px',
                    fontSize: isTablet ? '14px' : '12px',
                    fontWeight: isActive ? 600 : 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: isActive ? '#E8650A' : '#555555',
                    borderBottom: isActive ? '2px solid #E8650A' : '2px solid transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: index === 0 ? '0px' : (isTablet ? '6px' : '3px'),
                  }}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                >
                  <Icon 
                    size={isTablet ? (item.iconOnly ? 20 : 16) : (item.iconOnly ? 16 : 13)} 
                    style={{ 
                      color: isActive ? '#E8650A' : '#888888',
                    }}
                  />
                  {!item.iconOnly && <span>{item.label}</span>}
                </Link>
              );
            })
            )}
          </div>
        </div>
      </div>

      {/* ═══ DESKTOP: Navigation (pas d'animation immersive) ═══ */}
      <div className="hidden lg:block max-w-7xl mx-auto px-4">
        <div className="flex items-center">
          {NAV_ITEMS.map((item) => {
            const isActive = currentPath === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            
            // Univers avec dropdown
            if (item.isUniversDropdown) {
              return (
                <div key={item.path} className="relative" ref={universDropdownRef}>
                  <button
                    onClick={() => setShowUniversDropdown(!showUniversDropdown)}
                    className={`navbar-item navbar-item-desktop relative flex items-center gap-2 whitespace-nowrap transition-all ${isActive ? 'active' : ''}`}
                    data-testid="nav-univers-dropdown"
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                    <ChevronDown 
                      size={14} 
                      className={`transition-transform ${showUniversDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {/* Dropdown Univers */}
                  {showUniversDropdown && (
                    <div 
                      className="absolute top-full left-0 mt-1 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[100]"
                      style={{ width: '380px', maxHeight: '70vh' }}
                    >
                      {/* Header */}
                      <div className="p-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #e8f4fc 0%, #f0f7ff 100%)' }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-[#1a2a4a]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '15px' }}>
                              1 compte certifié = 20 univers
                            </h3>
                            <p className="text-sm font-semibold text-[#E8650A]">Bienvenue chez toi.</p>
                          </div>
                          <button 
                            onClick={() => setShowUniversDropdown(false)}
                            className="p-2 rounded-full hover:bg-gray-100"
                          >
                            <X size={18} color="#666" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Liste scrollable */}
                      <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 100px)' }}>
                        {UNIVERS_DATA.map((group) => (
                          <div key={group.group}>
                            <div className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                              {group.group}
                            </div>
                            {group.items.map((univers) => (
                              <button
                                key={univers.id}
                                onClick={() => handleUniversClick(univers)}
                                disabled={!univers.active}
                                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
                                  univers.active ? 'cursor-pointer' : 'cursor-default opacity-70'
                                } ${univers.active ? 'bg-gradient-to-r from-orange-50 to-transparent border-l-[3px] border-[#E8650A]' : ''}`}
                              >
                                <div 
                                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                                  style={{ 
                                    background: univers.iconBg,
                                    boxShadow: univers.active ? '0 4px 12px rgba(232,101,10,0.3)' : 'none'
                                  }}
                                >
                                  {univers.icon}
                                </div>
                                <div className="flex-1">
                                  <div className="font-semibold text-sm text-[#1a2a4a]">{univers.name}</div>
                                  <div className={`text-xs ${univers.active ? 'text-[#E8650A]' : 'text-[#1a2a4a]'}`}>
                                    {univers.desc}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span 
                                    className="text-[10px] font-semibold px-2 py-1 rounded"
                                    style={{ 
                                      background: univers.badgeColor || '#f0f0f0',
                                      color: univers.badgeColor ? '#fff' : '#999'
                                    }}
                                  >
                                    {univers.badge}
                                  </span>
                                  {univers.active && <span className="text-[#E8650A] font-bold">→</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`navbar-item navbar-item-desktop relative flex items-center gap-2 whitespace-nowrap transition-all ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                <Icon size={item.iconOnly ? 20 : 18} />
                {!item.iconOnly && <span>{item.label}</span>}
              </Link>
            );
          })}
        </div>
      </div>
      
      {/* ═══ SOUS-MENU MARKETPLACE HORIZONTAL (Desktop/Tablette) ═══ */}
      {isMarketplaceSection && (
        <div className="hidden md:block bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {MARKETPLACE_SUBMENU.map((subItem, idx) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path && location.pathname + location.search === subItem.path;
                
                if (subItem.isRayonsButton) {
                  return (
                    <button
                      key="rayons-btn"
                      onClick={() => onOpenRayonsMenu?.()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                      data-testid="submenu-rayons"
                    >
                      <SubIcon size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                if (subItem.isFilterButton) {
                  return (
                    <button
                      key="filter-btn"
                      onClick={() => onOpenFilters?.()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
                      data-testid="submenu-filters"
                    >
                      <SlidersHorizontal size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      isSubActive 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`submenu-${idx}`}
                  >
                    <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-500'} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SOUS-MENU ARTISANS HORIZONTAL (Desktop/Tablette) ═══ */}
      {isArtisansSection && (
        <div className="hidden md:block bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {ARTISANS_SUBMENU.map((subItem, idx) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path && location.pathname === subItem.path;
                
                // Bouton "Trouve ton artisan en 3 clics" - fond bleu - OUVRE LE FILTRE
                if (subItem.isFilterButton) {
                  return (
                    <button
                      key="filter-artisans-btn"
                      onClick={() => onOpenFilters?.()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
                      data-testid={`submenu-artisans-filter`}
                    >
                      <SlidersHorizontal size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                // Bouton "Reçois jusqu'à 5 devis en 24h" - fond bleu marine charte
                if (subItem.isDevisButton) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                      data-testid={`submenu-artisans-${idx}`}
                    >
                      <SubIcon size={16} />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Bouton "Carte" - fond vert émeraude
                if (subItem.isCarteButton) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#059669] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                      data-testid="submenu-artisans-carte"
                    >
                      <MapPin size={16} />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Bouton "En cours de développement" avec tooltip
                if (subItem.isComingSoon) {
                  return (
                    <div key="coming-soon-btn" className="relative group">
                      <button
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-400 font-medium rounded-lg cursor-not-allowed whitespace-nowrap"
                        disabled
                        data-testid={`submenu-artisans-${idx}`}
                      >
                        <SubIcon size={16} />
                        <span>{subItem.label}</span>
                      </button>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E3A5F] text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg z-50">
                        En cours de développement
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1E3A5F]"></div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      isSubActive 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`submenu-artisans-${idx}`}
                  >
                    <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-500'} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SOUS-MENU PARTICULIERS HORIZONTAL (Desktop/Tablette) ═══ */}
      {isParticuliersSection && (
        <div className="hidden md:block bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {PARTICULIERS_SUBMENU.map((subItem, idx) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path && location.pathname === subItem.path;
                
                if (subItem.isCategoriesButton) {
                  return (
                    <button
                      key="categories-btn"
                      onClick={() => onOpenParticuliersCategories?.()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                      data-testid="submenu-categories-particuliers"
                    >
                      <SubIcon size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                if (subItem.isParticuliersFilterButton) {
                  return (
                    <button
                      key="filter-particuliers-btn"
                      onClick={() => onOpenParticuliersFilters?.()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
                      data-testid="submenu-filters-particuliers"
                    >
                      <SlidersHorizontal size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      isSubActive 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`submenu-particuliers-${idx}`}
                  >
                    <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-500'} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SOUS-MENU RÉSEAU HORIZONTAL (Desktop/Tablette) ═══ */}
      {isReseauSection && (
        <div className="hidden md:block bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {RESEAU_SUBMENU.map((subItem, idx) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path && location.pathname === subItem.path;
                
                // Fil d'actualité - Orange clair (comme Accueil Particuliers)
                if (subItem.isAccueilReseau) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        isSubActive 
                          ? 'bg-[#F97316]/10 text-[#F97316]' 
                          : 'bg-[#F97316]/10 text-[#F97316] hover:bg-[#F97316]/20'
                      }`}
                      data-testid={`submenu-reseau-${idx}`}
                    >
                      <SubIcon size={16} className="text-[#F97316]" />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Messages Réseau - Orange vif
                if (subItem.isMessagesReseau) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                      data-testid={`submenu-reseau-${idx}`}
                    >
                      <SubIcon size={16} />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Cinéma - Bleu clair (comme Trouve ton annonce Particuliers)
                if (subItem.isCinemaReseau) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
                      data-testid={`submenu-reseau-${idx}`}
                    >
                      <SubIcon size={16} />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Autres items - Style standard
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      isSubActive 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`submenu-reseau-${idx}`}
                  >
                    <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-500'} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SOUS-MENU EMPLOI HORIZONTAL (Desktop/Tablette) ═══ */}
      {isEmploiSection && (
        <div className="hidden md:block bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {EMPLOI_SUBMENU.map((subItem, idx) => {
                const SubIcon = subItem.icon;
                const isSubActive = subItem.path && location.pathname === subItem.path;
                
                // Toutes les offres BTP - Orange clair (comme Accueil)
                if (subItem.isAccueilEmploi) {
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        isSubActive 
                          ? 'bg-[#F97316]/10 text-[#F97316]' 
                          : 'bg-[#F97316]/10 text-[#F97316] hover:bg-[#F97316]/20'
                      }`}
                      data-testid={`submenu-emploi-${idx}`}
                    >
                      <SubIcon size={16} className="text-[#F97316]" />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                }
                
                // Trouve ton emploi en 3 clics - Bleu clair
                if (subItem.isEmploiFilterButton) {
                  return (
                    <button
                      key="filter-emploi-btn"
                      onClick={() => {/* TODO: ouvrir filtres emploi */}}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0C4A6E] font-bold rounded-lg transition-colors whitespace-nowrap border-2 border-[#0369A1]"
                      data-testid="submenu-filters-emploi"
                    >
                      <SlidersHorizontal size={16} />
                      <span>{subItem.label}</span>
                    </button>
                  );
                }
                
                // Autres items - Style standard
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      isSubActive 
                        ? 'bg-[#F97316]/10 text-[#F97316]' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`submenu-emploi-${idx}`}
                  >
                    <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-500'} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* CSS Navbar */}
      <style>{`
        /* ── SCROLLBAR HIDE ── */
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        
        /* ── ITEM INACTIF ── */
        .navbar-item {
          color: #555555;
          border-bottom: 2px solid transparent;
          background: none;
        }
        .navbar-item svg {
          color: #888888;
        }
        
        /* ── ITEM ACTIF ── */
        .navbar-item.active {
          color: #E8650A;
          border-bottom: 2px solid #E8650A;
          background: none;
          font-weight: 600;
        }
        .navbar-item.active svg {
          color: #E8650A;
        }
        
        /* ── HOVER ── */
        @media (hover: hover) {
          .navbar-item:hover {
            color: #E8650A;
          }
          .navbar-item:hover svg {
            color: #E8650A;
          }
        }
        
        /* ── MOBILE (jusqu'à 768px) ── */
        @media (max-width: 768px) {
          .navbar-item {
            font-size: 12px;
            height: 44px;
          }
        }
        
        /* ── TABLETTE (769px à 1023px) - Style Premium ── */
        @media (min-width: 769px) and (max-width: 1023px) {
          .navbar-item {
            font-size: 15px;
            padding: 14px 20px;
            font-weight: 500;
            letter-spacing: 0.2px;
          }
          .navbar-item svg {
            width: 18px;
            height: 18px;
          }
          .navbar-item.active {
            font-weight: 600;
          }
        }
        
        /* ── DESKTOP (1024px+) ── */
        @media (min-width: 1024px) {
          .navbar-item {
            font-size: 14px;
            padding: 14px 18px;
          }
        }
      `}</style>
    </nav>
  );
}
